import React, { useMemo, useRef, useState } from "react";
import DashboardLayout from "../../layouts/Dashboard";
import { dashboardCRUD } from "../../http";
import { yearEnd, yearStart } from "../../functions/dateHelpers";
import FilterDatePicker from "../../components/DatePicker/FilterDatePiecker";
import { Stack, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  NATURAL_FOCI_COLUMNS,
  displayCell,
  exportNaturalFociExcel,
  groupNaturalFociRows,
  headerGroups,
  mergeScheduleWithFacts,
  parseNaturalFociExcel,
  sumField,
} from "./naturalFociSchedule";

const tableWrapStyle = {
  width: "100%",
  overflowX: "auto",
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  background: "#fff",
};

const tableStyle = {
  width: "max-content",
  minWidth: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
};

const thStyle = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  background: "#fafafa",
  border: "1px solid #e0e0e0",
  padding: "6px 8px",
  whiteSpace: "nowrap",
  fontWeight: 600,
  textAlign: "center",
  verticalAlign: "middle",
};

const tdStyle = {
  border: "1px solid #eee",
  padding: "5px 8px",
  verticalAlign: "top",
  whiteSpace: "nowrap",
};

const DddFullReport = () => {
  const start = yearStart();
  const end = yearEnd();
  const fileInputRef = useRef(null);

  const [query, setQuery] = useState({
    paging: { skip: 0 },
    filter: {
      researchEndDateTime: {
        operand1: start,
        operand2: end,
        operator: "between",
      },
    },
  });

  const [scheduleRows, setScheduleRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importInfo, setImportInfo] = useState("");

  const groups = useMemo(() => headerGroups(), []);
  const groupedData = useMemo(() => groupNaturalFociRows(rows), [rows]);

  const fetchAsync = async () => {
    setLoading(true);
    try {
      const response = await dashboardCRUD.post(
        "requestTarifications/quantity/indices/ddd",
        query
      );
      const merged = mergeScheduleWithFacts(scheduleRows, response || []);
      setRows(merged);
    } catch (e) {
      console.error(e);
      alert("Не удалось сформировать отчёт");
    } finally {
      setLoading(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImportLoading(true);
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseNaturalFociExcel(buffer);
      setScheduleRows(parsed);
      setRows(parsed);
      setImportInfo(`Загружен график: ${file.name} (${parsed.length} строк)`);
    } catch (e) {
      console.error(e);
      alert(e?.message || "Ошибка чтения Excel-файла");
    } finally {
      setImportLoading(false);
    }
  };

  const handleExport = () => {
    if (!rows.length) {
      alert("Нет данных для выгрузки");
      return;
    }
    setExportLoading(true);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      exportNaturalFociExcel(rows, `Природные_очаги_график_${stamp}.xlsx`);
    } catch (e) {
      console.error(e);
      alert("Ошибка при экспорте Excel");
    } finally {
      setExportLoading(false);
    }
  };

  const getRegionTotal = (regionRows, field) => {
    const total = sumField(regionRows, field);
    return total ? (Number.isInteger(total) ? total : total.toFixed(2)) : "";
  };

  const getGlobalTotal = (field) => {
    const total = sumField(rows, field);
    return total ? (Number.isInteger(total) ? total : total.toFixed(2)) : "";
  };

  return (
    <DashboardLayout>
      <Stack spacing={1.5}>
        <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>
          Природные очаги — график отчетности
        </Typography>

        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
          <label style={{ minWidth: 120 }}>Период</label>
          <FilterDatePicker
            label="от"
            value={query.filter.researchEndDateTime.operand1}
            handleFunction={(value) => {
              if (value) {
                query.filter.researchEndDateTime.operand1 = value;
                setQuery({ ...query });
              }
            }}
            style={{ width: "220px" }}
          />
          <FilterDatePicker
            label="до"
            value={query.filter.researchEndDateTime.operand2}
            handleFunction={(value) => {
              if (value) {
                query.filter.researchEndDateTime.operand2 = value;
                setQuery({ ...query });
              }
            }}
            style={{ width: "220px" }}
          />
          <LoadingButton loading={loading} variant="contained" onClick={fetchAsync}>
            Сформировать
          </LoadingButton>
          <LoadingButton
            loading={importLoading}
            variant="outlined"
            onClick={handleImportClick}
          >
            Загрузить график Excel
          </LoadingButton>
          <LoadingButton
            loading={exportLoading}
            variant="outlined"
            color="success"
            onClick={handleExport}
          >
            Скачать Excel
          </LoadingButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
        </Stack>

        {importInfo && (
          <Typography variant="body2" color="text.secondary">
            {importInfo}. При нажатии «Сформировать» факт и % выполнения
            подставляются из системы по филиалу и коду услуги.
          </Typography>
        )}
      </Stack>

      <div style={{ height: 12 }} />

      <div style={tableWrapStyle}>
        <table style={tableStyle} className="ant-table">
          <thead className="ant-table-thead">
            <tr>
              {groups.map((g, i) =>
                g.type === "single" ? (
                  <th key={`s-${g.col.key}`} rowSpan={2} style={thStyle}>
                    {g.col.shortTitle || g.col.title}
                  </th>
                ) : (
                  <th
                    key={`g-${g.title}-${i}`}
                    colSpan={g.cols.length}
                    style={thStyle}
                  >
                    {g.title}
                  </th>
                )
              )}
            </tr>
            <tr>
              {groups
                .filter((g) => g.type === "group")
                .flatMap((g) =>
                  g.cols.map((col) => (
                    <th key={`sub-${col.key}`} style={thStyle}>
                      {col.shortTitle || col.title}
                    </th>
                  ))
                )}
            </tr>
          </thead>
          <tbody className="ant-table-tbody">
            {Object.entries(groupedData).map(([region, regionRows]) => (
              <React.Fragment key={region}>
                {regionRows.map((row, i) => (
                  <tr key={`${region}-${i}`} className="ant-table-row">
                    {NATURAL_FOCI_COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className="ant-table-cell"
                        style={{
                          ...tdStyle,
                          whiteSpace:
                            col.key === "chemical" ||
                            col.key === "nozology" ||
                            col.key === "method" ||
                            col.key === "kind" ||
                            col.key === "filial"
                              ? "normal"
                              : "nowrap",
                          maxWidth:
                            col.key === "chemical" || col.key === "nozology"
                              ? 180
                              : col.key === "filial"
                              ? 220
                              : undefined,
                        }}
                      >
                        {displayCell(row, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr
                  className="ant-table-row"
                  style={{ backgroundColor: "#f6ffed" }}
                >
                  <td style={tdStyle} />
                  <td style={tdStyle} colSpan={5}>
                    Всего по филиалу:
                  </td>
                  <td style={tdStyle}>{getRegionTotal(regionRows, "yearPlan")}</td>
                  <td style={tdStyle}>{getRegionTotal(regionRows, "tourVolume")}</td>
                  <td style={tdStyle}>{getRegionTotal(regionRows, "fact")}</td>
                  <td style={tdStyle} />
                  <td style={tdStyle} />
                </tr>
              </React.Fragment>
            ))}
            {rows.length > 0 && (
              <tr
                className="ant-table-row"
                style={{ backgroundColor: "#d9f7be" }}
              >
                <td style={tdStyle} />
                <td style={tdStyle} colSpan={5}>
                  <strong>Общий итог:</strong>
                </td>
                <td style={tdStyle}>{getGlobalTotal("yearPlan")}</td>
                <td style={tdStyle}>{getGlobalTotal("tourVolume")}</td>
                <td style={tdStyle}>{getGlobalTotal("fact")}</td>
                <td style={tdStyle} />
                <td style={tdStyle} />
              </tr>
            )}
            {!rows.length && (
              <tr>
                <td
                  style={{ ...tdStyle, whiteSpace: "normal", padding: 16 }}
                  colSpan={NATURAL_FOCI_COLUMNS.length}
                >
                  Загрузите график Excel или нажмите «Сформировать» для данных за период.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
};

export default DddFullReport;
